<template>
  <main class="opc-manage-page">
    <section class="soft-card table-panel">
      <div class="toolbar">
        <el-input
          v-model="searchQuery"
          placeholder="搜索账号、姓名、邮箱..."
          size="small"
          clearable
          class="search-input"
          @keyup.enter.native="onSearch" @clear="onSearch"
        />
        <el-select v-model="statusFilter" size="small" placeholder="状态" clearable class="filter-select" @change="onSearch">
          <el-option label="全部" value="" />
          <el-option label="已激活" value="已激活" />
          <el-option label="已禁用" value="已禁用" />
          <el-option label="未激活" value="未激活" />
        </el-select>
        <el-select v-model="accountTypeFilter" size="small" placeholder="账号类型" clearable class="filter-select" @change="onSearch">
          <el-option label="全部" value="" />
          <el-option label="用户账号" value="user" />
          <el-option label="智能体账号" value="agent" />
        </el-select>
        <el-button size="small" plain icon="el-icon-search" @click="onSearch">搜索</el-button>
        <el-button size="small" icon="el-icon-download" @click="onExport">导出</el-button>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="onAdd">新增</el-button>
        <el-button size="small" icon="el-icon-s-operation" @click="openBatchAssign">批量分配模型</el-button>
      </div>

      <div class="desktop-only">
        <el-table :data="accounts" size="small" style="width: 100%" @sort-change="onSortChange" @selection-change="onSelectionChange">
          <el-table-column type="selection" width="45" />
          <el-table-column type="index" label="序号" width="60" :index="pageIndex" />
          <el-table-column prop="username" label="账号" width="120" show-overflow-tooltip />
          <el-table-column prop="name" label="姓名" width="120" show-overflow-tooltip />
          <el-table-column prop="email" label="邮箱" min-width="180" show-overflow-tooltip />
          <el-table-column label="总 token" width="140" sortable="custom" prop="totalTokens">
            <template slot-scope="scope">{{ $formatNumber(scope.row.totalTokens) }}</template>
          </el-table-column>
          <el-table-column label="总费用" width="140" sortable="custom" prop="totalCost">
            <template slot-scope="scope">¥ {{ $formatNumber(scope.row.totalCost) }}</template>
          </el-table-column>
          <el-table-column label="余额" width="150">
            <template slot-scope="scope">
              <span :class="{ 'balance-over': Number(scope.row.balance) <= 0 }">¥ {{ $formatNumber(scope.row.balance) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="本月消费" width="140">
            <template slot-scope="scope">¥ {{ $formatNumber(scope.row.monthCost) }}</template>
          </el-table-column>
          <el-table-column label="API Key" min-width="200" show-overflow-tooltip>
            <template slot-scope="scope">
              <code class="key-text">{{ scope.row.apiKey }}</code>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag size="small" :type="statusTagType(scope.row.status)">{{ scope.row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" width="180">
            <template slot-scope="scope">{{ $formatTime(scope.row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="280" fixed="right">
            <template slot-scope="scope">
              <el-button size="small" type="text" style="color:#67c23a;" @click="onRecharge(scope.row)">充值</el-button>
              <el-button size="small" type="text" @click="onBalanceLog(scope.row)">余额记录</el-button>
              <el-button size="small" type="text" @click="onEdit(scope.row)">编辑</el-button>
              <el-button v-if="scope.row.status !== '未激活'" size="small" type="text" style="color:#409eff;" @click="onResetPassword(scope.row)">重置密码</el-button>
              <el-button v-if="scope.row.status === '未激活'" size="small" type="text" style="color:#e6a23c;" @click="onResend(scope.row)">重新发送</el-button>
              <el-button v-if="scope.row.status !== '未激活'" size="small" type="text" :class="{ 'delete-btn': true, 'enable-btn': scope.row.status === '已禁用' }" @click="onToggleStatus(scope.row)">
                {{ scope.row.status === '已禁用' ? '启用' : '禁用' }}
              </el-button>
              <el-button size="small" type="text" class="delete-btn" @click="onDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          class="pagination-bar"
          layout="total, prev, pager, next"
          small
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="onPageChange"
        />
      </div>

      <div class="mobile-only list-cards">
        <div v-for="account in accounts" :key="account.id" class="list-card">
          <div class="card-header">
            <div>
              <div class="card-title">{{ account.name || account.username }}</div>
              <div class="card-sub">{{ account.username }} · {{ account.email }}</div>
            </div>
            <el-tag size="small" :type="statusTagType(account.status)">{{ account.status }}</el-tag>
          </div>
          <div class="stats-row">
            <div class="stat-item">
              <div class="stat-value">{{ $formatNumber(account.totalTokens) }}</div>
              <div class="stat-label">总 token</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">¥ {{ $formatNumber(account.totalCost) }}</div>
              <div class="stat-label">总费用</div>
            </div>
            <div class="stat-item">
              <div class="stat-value" :class="{ 'balance-over': Number(account.balance) <= 0 }">¥ {{ $formatNumber(account.balance) }}</div>
              <div class="stat-label">余额</div>
            </div>
          </div>
          <div class="meta-row">
            <span>本月消费：¥ {{ $formatNumber(account.monthCost) }}</span>
            <span>{{ $formatTime(account.createdAt) }}</span>
          </div>
          <div class="card-actions">
            <el-button size="small" type="text" style="color:#67c23a;" @click="onRecharge(account)">充值</el-button>
            <el-button size="small" type="text" @click="onBalanceLog(account)">余额记录</el-button>
            <el-button size="small" type="text" @click="onEdit(account)">编辑</el-button>
            <el-button v-if="account.status !== '未激活'" size="small" type="text" style="color:#409eff;" @click="onResetPassword(account)">重置密码</el-button>
            <el-button v-if="account.status === '未激活'" size="small" type="text" style="color:#e6a23c;" @click="onResend(account)">重新发送</el-button>
            <el-button v-if="account.status !== '未激活'" size="small" type="text" :class="{ 'delete-btn': true, 'enable-btn': account.status === '已禁用' }" @click="onToggleStatus(account)">
              {{ account.status === '已禁用' ? '启用' : '禁用' }}
            </el-button>
            <el-button size="small" type="text" class="delete-btn" @click="onDelete(account)">删除</el-button>
          </div>
        </div>
        <el-pagination
          class="pagination-bar"
          layout="total, prev, pager, next"
          small
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="onPageChange"
        />
      </div>
    </section>

    <!-- 新增/编辑账号 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px" :close-on-click-modal="false" top="20vh">
      <el-form ref="form" :model="form" :rules="rules" label-width="80px" size="small">
        <el-form-item label="账号" prop="username">
          <el-input v-model="form.username" placeholder="用于登录" />
        </el-form-item>
        <el-form-item label="姓名" prop="name">
          <el-input v-model="form.name" placeholder="" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="form.email" placeholder="用于接收激活邮件" />
        </el-form-item>
        <el-form-item>
          <div style="margin-bottom:6px;font-size:13px;color:var(--muted);font-weight:600;">分配模型</div>
          <el-checkbox-group v-model="form.modelIds">
            <el-checkbox v-for="m in availableModels" :key="m.supplierModelId" :label="m.supplierModelId" style="margin-right:12px;margin-bottom:6px;">
              {{ m.name }}
            </el-checkbox>
          </el-checkbox-group>
          <div v-if="availableModels.length === 0" style="font-size:12px;color:var(--muted-2);">暂无可分配模型</div>
        </el-form-item>
        <el-form-item v-if="form.apiKey">
          <div style="font-size:12px;color:var(--muted);">
            <span style="color:var(--muted-2);">API Key：</span>
            <code style="color:#8eddf8;background:rgba(50,200,245,0.1);padding:2px 6px;border-radius:4px;">{{ form.apiKey }}</code>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false" size="small">取消</el-button>
        <el-button type="primary" @click="submitForm" size="small" :loading="submitting">确定</el-button>
      </span>
    </el-dialog>

    <!-- 批量分配模型弹窗 -->
    <el-dialog title="批量分配模型" :visible.sync="batchDialogVisible" width="520px" :close-on-click-modal="false" top="20vh">
      <div style="margin-bottom:16px;">
        <div style="margin-bottom:8px;font-size:13px;color:var(--muted);">应用范围</div>
        <el-radio-group v-model="batchScope">
          <el-radio label="all">OPC 客户下所有账号</el-radio>
          <el-radio label="selected">选中的账号（{{ selectedAccounts.length }} 个）</el-radio>
        </el-radio-group>
      </div>
      <div style="margin-bottom:8px;font-size:13px;color:var(--muted);font-weight:600;">选择模型</div>
      <el-checkbox-group v-model="batchModelIds" style="margin-bottom:12px;">
        <el-checkbox v-for="m in availableModels" :key="m.supplierModelId" :label="m.supplierModelId" style="margin-right:12px;margin-bottom:6px;">
          {{ m.name }}
        </el-checkbox>
      </el-checkbox-group>
      <div v-if="availableModels.length === 0" style="font-size:12px;color:var(--muted-2);">暂无可分配模型</div>
      <div style="font-size:12px;color:var(--muted-2);margin-top:8px;">
        已有权限的账号会自动跳过，不会重复分配
      </div>
      <span slot="footer">
        <el-button @click="batchDialogVisible = false" size="small">取消</el-button>
        <el-button type="primary" @click="submitBatchAssign" size="small" :loading="batchSubmitting"
                   :disabled="batchModelIds.length === 0 || (batchScope === 'selected' && selectedAccounts.length === 0)">
          确定
        </el-button>
      </span>
    </el-dialog>

    <!-- 按账号充值对话框 -->
    <el-dialog title="个人账号充值" :visible.sync="rechargeDialogVisible" width="420px" :close-on-click-modal="false" top="25vh">
      <el-form ref="rechargeForm" :model="rechargeForm" :rules="rechargeRules" label-width="80px" size="small">
        <el-form-item label="充值账号">
          <span style="font-weight:600;">{{ rechargeAccountName }}</span>
        </el-form-item>
        <el-form-item label="当前余额">
          <span style="color:#10b981;">¥ {{ $formatNumber(rechargeCurrentBalance) }}</span>
        </el-form-item>
        <el-form-item prop="amount" label="充值金额">
          <el-input v-model="rechargeForm.amount" placeholder="请输入充值金额" type="text">
            <template slot="append">元</template>
          </el-input>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="rechargeForm.remark" placeholder="可选备注" type="text" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="rechargeDialogVisible = false" size="small">取消</el-button>
        <el-button type="primary" @click="submitRecharge" size="small" :loading="rechargeSubmitting">确认充值</el-button>
      </div>
    </el-dialog>

    <!-- 余额记录对话框 -->
    <el-dialog title="账号余额记录" :visible.sync="balanceLogDialogVisible" width="850px" :close-on-click-modal="false" top="8vh">
      <div style="margin-bottom:10px;font-size:13px;">
        账号：<strong>{{ balanceLogAccountName }}</strong>
        &nbsp;&nbsp;当前余额：<strong style="color:#10b981;">¥ {{ $formatNumber(balanceLogCurrentBalance) }}</strong>
      </div>
      <el-table :data="balanceLogs" size="small" style="width:100%" :max-height="500">
        <el-table-column prop="type" label="类型" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.type === 'RECHARGE'" type="success" size="small">充值</el-tag>
            <el-tag v-else type="danger" size="small">扣费</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="金额" width="140">
          <template slot-scope="scope">
            <span :style="{ color: Number(scope.row.amount) >= 0 ? '#10b981' : '#f56c6c' }">
              {{ Number(scope.row.amount) >= 0 ? '+' : '' }}¥ {{ $formatNumber(scope.row.amount) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作前余额" width="160">
          <template slot-scope="scope"><span style="white-space:nowrap;">¥ {{ $formatNumber(scope.row.balanceBefore) }}</span></template>
        </el-table-column>
        <el-table-column label="操作后余额" width="160">
          <template slot-scope="scope"><span style="white-space:nowrap;">¥ {{ $formatNumber(scope.row.balanceAfter) }}</span></template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip />
        <el-table-column label="时间" width="180">
          <template slot-scope="scope">{{ $formatTime(scope.row.createdAt) }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="balanceLogTotal"
        :page-size="balanceLogPageSize"
        :current-page.sync="balanceLogPage"
        @current-change="onBalanceLogPageChange"
      />
    </el-dialog>
  </main>
</template>

<script>
import * as XLSX from 'xlsx'

export default {
  name: 'OpcManagePage',
  data() {
    return {
      accounts: [],
      availableModels: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      searchQuery: '',
      sortField: '',
      sortOrder: '',
      statusFilter: '',
      accountTypeFilter: '',
      // 新增/编辑
      dialogVisible: false,
      dialogTitle: '',
      submitting: false,
      selectedAccounts: [],
      batchDialogVisible: false,
      batchScope: 'all',
      batchModelIds: [],
      batchSubmitting: false,
      form: {
        id: null,
        username: '',
        name: '',
        email: '',
        apiKey: '',
        modelIds: []
      },
      rules: {
        username: [
          { required: true, message: '请输入账号', trigger: 'blur' }
        ],
        name: [
          { required: true, message: '请输入姓名', trigger: 'blur' }
        ],
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { type: 'email', message: '请输入有效的邮箱地址', trigger: 'blur' }
        ]
      },
      // 充值
      rechargeDialogVisible: false,
      rechargeSubmitting: false,
      rechargeAccountId: null,
      rechargeAccountName: '',
      rechargeCurrentBalance: 0,
      rechargeForm: { amount: '', remark: '' },
      rechargeRules: {
        amount: [
          { required: true, message: '请输入充值金额', trigger: 'blur' },
          { pattern: /^[1-9]\d*\.?\d*$|^0\.\d+$/, message: '请输入有效金额', trigger: 'blur' }
        ]
      },
      // 余额记录
      balanceLogDialogVisible: false,
      balanceLogAccountId: null,
      balanceLogAccountName: '',
      balanceLogCurrentBalance: 0,
      balanceLogs: [],
      balanceLogTotal: 0,
      balanceLogPageSize: 10,
      balanceLogPage: 1
    }
  },
  computed: {
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    }
  },
  async mounted() {
    await this.fetchAccounts()
    this.fetchAvailableModels()
  },
  methods: {
    statusTagType(status) {
      if (status === '已激活') return 'success'
      if (status === '已禁用') return 'info'
      return 'warning'
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchAccounts()
    },
    onSearch() {
      this.currentPage = 1
      this.fetchAccounts()
    },
    onSortChange({ prop, order }) {
      if (prop && order) {
        this.sortField = prop === 'totalTokens' ? 'total_tokens' : 'total_cost'
        this.sortOrder = order === 'ascending' ? 'asc' : 'desc'
      } else {
        this.sortField = ''
        this.sortOrder = ''
      }
      this.currentPage = 1
      this.fetchAccounts()
    },
    async fetchAccounts() {
      try {
        const res = await this.$http.get('/admin/opc/accounts', {
          params: {
            keyword: this.searchQuery || undefined,
            page: this.currentPage,
            pageSize: this.pageSize,
            sortField: this.sortField || undefined,
            sortOrder: this.sortOrder || undefined,
            status: this.statusFilter || undefined,
            accountType: this.accountTypeFilter || undefined
          }
        })
        this.accounts = res.accounts || []
        this.total = Number(res.total) || 0
      } catch (e) {
        this.$message.error(e.response?.data?.error || '加载账号列表失败')
      }
    },
    async fetchAvailableModels() {
      try {
        const res = await this.$http.get('/admin/opc/models', {
          params: { page: 1, pageSize: 1000 }
        })
        this.availableModels = res.records || []
      } catch {
        this.$message.error('加载模型列表失败')
      }
    },
    onAdd() {
      this.dialogTitle = '新增个人账号'
      this.form = { id: null, username: '', name: '', email: '', apiKey: '', modelIds: [] }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
    },
    onEdit(row) {
      this.dialogTitle = '编辑个人账号'
      this.form = {
        id: row.id,
        username: row.username,
        name: row.name,
        email: row.email,
        apiKey: row.apiKey || '',
        modelIds: row.modelIds || []
      }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
    },
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = {
            username: this.form.username,
            name: this.form.name,
            email: this.form.email,
            modelIds: this.form.modelIds
          }
          if (this.form.id) {
            await this.$http.put('/admin/opc/accounts/' + this.form.id, payload)
            this.$message.success('编辑成功')
          } else {
            await this.$http.post('/admin/opc/accounts', payload)
            this.$message.success('新增成功，激活邮件已发送')
          }
          this.dialogVisible = false
          this.fetchAccounts()
        } catch (e) {
          this.$message.error(e.response?.data?.error || '操作失败')
        } finally {
          this.submitting = false
        }
      })
    },
    async onToggleStatus(row) {
      const action = row.status === '已禁用' ? '启用' : '禁用'
      try {
        await this.$http.put('/admin/opc/accounts/' + row.id + '/toggle-status')
        this.$message.success(action + '成功')
        this.fetchAccounts()
      } catch (e) {
        this.$message.error(e.response?.data?.error || '操作失败')
      }
    },
    async onResend(row) {
      try {
        const res = await this.$http.post('/admin/opc/accounts/' + row.id + '/send-code')
        this.$message.success(res.message || '验证码已重新发送')
      } catch (e) {
        this.$message.error(e.response?.data?.error || '重新发送失败')
      }
    },
    onResetPassword(row) {
      this.$confirm(`确定将账号「${row.name || row.username}」的密码重置为 123456 吗？`, '重置密码', {
        confirmButtonText: '重置',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return this.$http.put('/admin/opc/accounts/' + row.id + '/reset-password')
      }).then(res => {
        this.$message.success(res.message || '密码已重置为 123456')
      }).catch(e => {
        if (e === 'cancel' || e === 'close') return
        this.$message.error(e.response?.data?.error || '重置失败')
      })
    },
    onDelete(row) {
      this.$confirm('确定删除账号「' + row.name + '」？', '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return this.$http.delete('/admin/opc/accounts/' + row.id)
      }).then(() => {
        this.$message.success('删除成功')
        this.fetchAccounts()
      }).catch(e => {
        if (e === 'cancel' || e === 'close') return
        this.$message.error(e.response?.data?.error || '删除失败')
      })
    },
    onSelectionChange(selection) {
      this.selectedAccounts = selection
    },
    openBatchAssign() {
      this.batchModelIds = []
      this.batchScope = 'all'
      this.batchDialogVisible = true
    },
    async submitBatchAssign() {
      if (this.batchModelIds.length === 0) {
        this.$message.warning('请选择模型')
        return
      }
      if (this.batchScope === 'selected' && this.selectedAccounts.length === 0) {
        this.$message.warning('请先选中账号')
        return
      }
      this.batchSubmitting = true
      try {
        const payload = {
          modelIds: this.batchModelIds,
          accountIds: this.batchScope === 'selected' ? this.selectedAccounts.map(a => a.id) : null
        }
        const res = await this.$http.post('/admin/opc/accounts/batch-assign-models', payload)
        this.$message.success(`已为 ${res.count} 条账号-模型关系授权`)
        this.batchDialogVisible = false
        this.fetchAccounts()
      } catch (e) {
        this.$message.error(e.response?.data?.error || '批量分配失败')
      } finally {
        this.batchSubmitting = false
      }
    },
    async onExport() {
      try {
        const records = await this.$http.get('/admin/opc/accounts/export', {
          params: {
            keyword: this.searchQuery || undefined,
            status: this.statusFilter || undefined
          }
        })
        if (!records || !records.length) {
          this.$message.info('没有数据可导出')
          return
        }
        const num = v => (v == null || isNaN(Number(v)) ? 0 : Number(v))
        const data = records.map(r => ({
          '账号': r.username,
          '姓名': r.name,
          '邮箱': r.email,
          '总Token': num(r.totalTokens),
          '总费用': num(r.totalCost),
          '余额': num(r.balance),
          '本月消费': num(r.monthCost),
          'API Key': r.apiKey,
          '状态': r.status,
          '创建时间': (r.createdAt || '').replace('T', ' ').slice(0, 19),
          '可用模型': (r.modelNames || []).join('、')
        }))
        const ws = XLSX.utils.json_to_sheet(data)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'OPC账号列表')
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([wbout], { type: 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const ts = new Date()
        const pad = n => String(n).padStart(2, '0')
        a.download = `OPC账号列表_${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch {
        this.$message.error('导出失败')
      }
    },
    async onRecharge(row) {
      this.rechargeAccountId = row.id
      this.rechargeAccountName = row.name || row.username
      this.rechargeCurrentBalance = row.balance || 0
      this.rechargeForm = { amount: '', remark: '' }
      this.rechargeDialogVisible = true
      this.$nextTick(() => this.$refs.rechargeForm?.clearValidate())
    },
    async submitRecharge() {
      await this.$refs.rechargeForm.validate(async valid => {
        if (!valid) return
        this.rechargeSubmitting = true
        try {
          await this.$http.post('/admin/opc/recharge', {
            accountId: this.rechargeAccountId,
            amount: parseFloat(this.rechargeForm.amount),
            remark: this.rechargeForm.remark || ''
          })
          this.$message.success('充值申请已提交')
          this.rechargeDialogVisible = false
          this.fetchAccounts()
        } catch (e) {
          this.$message.error(e.response?.data?.error || '充值失败')
        } finally {
          this.rechargeSubmitting = false
        }
      })
    },
    async onBalanceLog(row) {
      this.balanceLogAccountId = row.id
      this.balanceLogAccountName = row.name || row.username
      this.balanceLogCurrentBalance = row.balance || 0
      this.balanceLogPage = 1
      this.balanceLogDialogVisible = true
      this.loadBalanceLogs()
    },
    onBalanceLogPageChange(page) {
      this.balanceLogPage = page
      this.loadBalanceLogs()
    },
    async loadBalanceLogs() {
      try {
        const res = await this.$http.get('/admin/opc/accounts/' + this.balanceLogAccountId + '/balance-logs', {
          params: { page: this.balanceLogPage, pageSize: this.balanceLogPageSize }
        })
        this.balanceLogs = res.records || []
        this.balanceLogTotal = Number(res.total) || 0
      } catch (e) {
        this.$message.error(e.response?.data?.error || '加载余额记录失败')
      }
    }
  }
}
</script>

<style scoped>
.opc-manage-page {
  padding: 18px 24px 76px;
}

.table-panel {
  padding: 16px;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.table-panel ::v-deep .el-table,
.table-panel ::v-deep .el-table__header-wrapper,
.table-panel ::v-deep .el-table__body-wrapper {
  border-radius: var(--radius);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.search-input {
  width: 220px;
}

.filter-select {
  width: 120px;
}

.key-text {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
  font-size: 13px;
  color: #8eddf8;
  background: rgba(50, 200, 245, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.delete-btn {
  color: #ff93a4;
}

.delete-btn:hover {
  color: #ffb6c1;
}

.enable-btn {
  color: var(--cyan);
}

.balance-over {
  color: #ff93a4;
  font-weight: 700;
}

.muted-text {
  font-size: 12px;
  color: var(--muted-2);
}

.list-cards { display: grid; gap: 14px; }

.mobile-only { display: none; }

@media (max-width: 760px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .opc-manage-page {
    padding: 12px 12px 28px;
  }
  .table-panel {
    padding: 10px;
    overflow-x: visible;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .search-input {
    flex: 1;
    min-width: 80px;
  }
  .pagination-bar {
    text-align: center;
  }
}
.list-card {
  padding: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(27,39,58,.92), rgba(20,30,46,.88));
  border: 1px solid rgba(133,164,255,.12);
  box-shadow: 0 8px 28px rgba(0,0,0,.22);
  overflow: hidden;
  transition: transform .18s ease, box-shadow .18s ease;
}
.list-card:active {
  transform: translateY(-1px);
  box-shadow: 0 12px 36px rgba(0,0,0,.3);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
}
.card-sub {
  font-size: 12px;
  color: rgba(255,255,255,.4);
  margin-top: 2px;
}
.stats-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.stat-item {
  flex: 1;
  min-width: 0;
  background: rgba(255,255,255,.04);
  border-radius: 8px;
  padding: 8px 10px;
  text-align: center;
}
.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
}
.stat-label {
  font-size: 10px;
  color: rgba(255,255,255,.35);
  margin-top: 2px;
  letter-spacing: .02em;
}
.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
  font-size: 11px;
  color: rgba(255,255,255,.35);
}
.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
}
.card-actions .el-button--text {
  font-size: 12px;
  padding: 4px 10px;
  height: auto;
}
</style>